import React from "react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

interface ToasterErrorBoundaryProps {
  children: React.ReactNode;
}

interface ToasterErrorBoundaryState {
  failed: boolean;
}

/**
 * Keeps a toast that fails to render from blanking the whole app (#926).
 *
 * The sonner <Toaster> is mounted once at the app root, beside the routes and
 * so outside every layout's RouteErrorBoundary (#920). When a toast's content
 * throws while rendering (e.g. an object passed to `toast.error`), React
 * unmounts the entire root into a white page. This boundary logs the error,
 * dismisses the queued toasts (sonner keeps them in a module-level store, so
 * remounting alone would re-render the same bad toast) and remounts the
 * Toaster so later toasts still show.
 */
export class ToasterErrorBoundary extends React.Component<
  ToasterErrorBoundaryProps,
  ToasterErrorBoundaryState
> {
  state: ToasterErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ToasterErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    logger.error("toast_render_error", { message: error.message, name: error.name });
    toast.dismiss();
    this.setState({ failed: false });
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
